import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IProfessionalAddress } from 'app/interfaces/address.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { AddressService } from 'app/services/address.service';
import { UserService } from 'app/services/user.service';

@Component({
	selector: 'app-address-card',
	templateUrl: './address-card.component.html',
	styleUrls: ['./address-card.component.scss'],
})
export class AddressCardComponent {
	isLoadingEdit = false;
	isLoadingRemove = false;
	isConfirmModalVisible = false;
	isWarningModalVisible = false;
	professionalId = null;
	warningModalType: 'edit' | 'remove' = 'edit';

	@Input() index: IProfessionalAddress;
	@Input() address: IProfessionalAddress;
	@Output() removedAddress: EventEmitter<string> = new EventEmitter<string>();

	constructor(
		public notifications: ZkNotificationService,
		private actionTrackerService: ActionTrackerService,
		public translateService: TranslateService,
		private addressService: AddressService,
		private router: Router,
		private user: UserService
	) {
		this.user.getUserId().then((res) => {
			this.professionalId = res;
		});
	}

	get formattedAddress(): string {
		return `${this.address.street}, ${this.address.number} - ${
			this.address.complement ? `${this.address.complement} - ` : ''
		}${this.address.neighborhood}, ${this.address.city}/${
			this.address.state
		} - ${this.address.postalCode.replace(/(\d{5})(\d)/, '$1-$2')}`;
	}

	get formattedPhone(): string {
		return this.address.phone;
	}

	private async hasAppointments(addressId: string): Promise<boolean> {
		let has = false;
		try {
			const res = await this.addressService
				.getAddressAppointmentsById(addressId)
				.toPromise();
			if (res.total > 0) {
				has = true;
			}
		} catch (err) {
			// XXX: API is returning 404 when there are no appointments
			if (err?.status === 404) {
				has = false;
			} else {
				console.error(err);
				this.notifications.showError(
					this.translateService.instant(
						'MY_ACCOUNT.ONSITE_SESSIONS.ERRORS.FETCH_APPOINTMENT_BY_ADDRESSS'
					)
				);
			}
		}
		return has;
	}

	goToEditPage() {
		this.router.navigateByUrl(`/account/address/edit/${this.address.id}`);
	}

	async onEditAddressClick() {
		this.isLoadingEdit = true;
		const hasAppointments = await this.hasAppointments(this.address.id);
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Account,
			SUBSCOPE_TYPE.Address,
			ITEM_TYPE.Edit,
			INTERACTION_TYPE.Click,
			{
				addressId: this.address.id,
				hasAppointments,
			}
		);
		if (hasAppointments) {
			this.isLoadingEdit = false;
			this.warningModalType = 'edit';
			this.isWarningModalVisible = true;
		} else {
			this.goToEditPage();
		}
	}

	removeAddress() {
		this.isLoadingRemove = true;
		this.addressService.removeAddress(this.address.id).subscribe({
			next: () => {
				this.isLoadingRemove = false;
				this.removedAddress.emit(this.address.id);
				this.notifications.showSuccess(
					this.translateService.instant(
						'MY_ACCOUNT.ONSITE_SESSIONS.SUCCESS.REMOVE_ADDRESS'
					)
				);
				this.actionTrackerService.eventTracker(
					SCOPE_TYPE.Account,
					SUBSCOPE_TYPE.Address,
					ITEM_TYPE.Remove,
					INTERACTION_TYPE.Success,
					{
						professionalId: this.professionalId,
						addressId: this.address.id,
					}
				);
			},
			error: (error) => {
				this.isLoadingRemove = false;
				console.error(error);
				this.actionTrackerService.eventTracker(
					SCOPE_TYPE.Account,
					SUBSCOPE_TYPE.Address,
					ITEM_TYPE.Remove,
					INTERACTION_TYPE.Error,
					{
						professionalId: this.professionalId,
						addressId: this.address.id,
						statusCode: error?.status,
					}
				);
				this.notifications.showError(
					this.translateService.instant(
						'MY_ACCOUNT.ONSITE_SESSIONS.ERRORS.REMOVE_ADDRESS'
					)
				);
			},
		});
	}

	async onRemoveAddressClick() {
		this.isLoadingRemove = true;
		const hasAppointments = await this.hasAppointments(this.address.id);
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Account,
			SUBSCOPE_TYPE.Address,
			ITEM_TYPE.Remove,
			INTERACTION_TYPE.Click,
			{
				addressId: this.address.id,
				hasAppointments,
			}
		);
		this.isLoadingRemove = false;
		if (hasAppointments) {
			this.warningModalType = 'remove';
			this.isWarningModalVisible = true;
		} else {
			this.isConfirmModalVisible = true;
		}
	}
}
